/**
 * Lo PURO de la pantalla de Athena (tanda 6): cómo se agrupa el historial, cómo se dice un
 * tamaño o un saludo, y las decisiones de lo que no se puede probar con un navegador de
 * mentira (grabar, arrastrar, la tecla Enter). La pantalla solo pinta lo que sale de aquí.
 */

/** El tope del servidor para un archivo (multipart): uno más pesado ni se manda. */
export const TOPE_DE_ADJUNTO = 25 * 1024 * 1024;
/** Cuántos archivos recibe el servidor en un mensaje. */
export const MAX_ADJUNTOS = 10;

// ───────────────────────────── el historial ─────────────────────────────

export type IdDeGrupo = 'hoy' | 'ayer' | 'semana' | 'antes';

export interface GrupoDeHilos<T> {
  id: IdDeGrupo;
  titulo: string;
  hilos: T[];
}

const GRUPOS: ReadonlyArray<{ id: IdDeGrupo; titulo: string }> = [
  { id: 'hoy', titulo: 'Hoy' },
  { id: 'ayer', titulo: 'Ayer' },
  { id: 'semana', titulo: 'Esta semana' },
  { id: 'antes', titulo: 'Antes' },
];

/**
 * Los hilos por fecha de su último mensaje: Hoy, Ayer, Esta semana (desde el lunes) y Antes.
 * Los días son los del reloj de quien mira, y cambian a medianoche. Un grupo sin hilos no sale;
 * dentro de cada uno, el más reciente arriba.
 */
export function agruparHilos<T extends { updatedAt: string }>(hilos: readonly T[], ahora: Date): GrupoDeHilos<T>[] {
  const medianoche = (mas: number) => new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate() + mas).getTime();
  const hoy = medianoche(0);
  const ayer = medianoche(-1);
  // `getDay()`: 0 es domingo. La semana empieza el lunes.
  const lunes = medianoche(-((ahora.getDay() + 6) % 7));
  const cuando = (h: T) => Date.parse(h.updatedAt) || 0;
  const grupoDe = (h: T): IdDeGrupo => {
    const t = cuando(h);
    if (t >= hoy) return 'hoy';
    if (t >= ayer) return 'ayer';
    return t >= lunes ? 'semana' : 'antes';
  };
  return GRUPOS.map((g) => ({ ...g, hilos: hilos.filter((h) => grupoDe(h) === g.id).sort((a, b) => cuando(b) - cuando(a)) })).filter(
    (g) => g.hilos.length > 0,
  );
}

const plano = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

/** El buscador del historial: por título y por vista previa, sin mirar tildes ni mayúsculas. */
export function filtrarHilos<T extends { titulo: string; vistaPrevia: string }>(hilos: readonly T[], texto: string): T[] {
  const q = plano(texto).replace(/\s+/g, ' ').trim();
  if (!q) return [...hilos];
  return hilos.filter((h) => plano(`${h.titulo} ${h.vistaPrevia}`).includes(q));
}

// ───────────────────────────── textos chicos ─────────────────────────────

/** «340 KB», «2.4 MB», «18 MB». */
export function tamanoLegible(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 KB';
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  const kb = bytes / 1024;
  if (kb < 1023.5) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb < 10 ? mb.toFixed(1).replace(/\.0$/, '') : Math.round(mb)} MB`;
}

/** «Buenas tardes, Richard». `hora` es la del reloj (0–23); del nombre se usa solo el primero. */
export function saludoDe(hora: number, nombre?: string | null): string {
  const saludo = hora >= 5 && hora < 12 ? 'Buenos días' : hora >= 12 && hora < 19 ? 'Buenas tardes' : 'Buenas noches';
  const primero = (nombre ?? '').trim().split(/\s+/)[0] ?? '';
  // Un correo no es un nombre: sin nombre, el saludo va solo.
  return primero && !primero.includes('@') ? `${saludo}, ${primero}` : saludo;
}

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/**
 * Cuándo se mandó un mensaje, discreto: de hoy, solo la hora («3:05 p. m.»); de otro día, con
 * su fecha («6 oct · 3:05 p. m.»), porque un hilo se retoma días después.
 */
export function horaDeMensaje(iso: string, ahora: Date): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const hora = `${d.getHours() % 12 || 12}:${String(d.getMinutes()).padStart(2, '0')} ${d.getHours() < 12 ? 'a. m.' : 'p. m.'}`;
  const mismoDia = d.getFullYear() === ahora.getFullYear() && d.getMonth() === ahora.getMonth() && d.getDate() === ahora.getDate();
  if (mismoDia) return hora;
  const anio = d.getFullYear() === ahora.getFullYear() ? '' : ` ${d.getFullYear()}`;
  return `${d.getDate()} ${MESES[d.getMonth()]}${anio} · ${hora}`;
}

/** El texto de Athena para «Copiar»: sin los asteriscos de las negritas, que pegados en WhatsApp salen tal cual. */
export function sinMarcas(texto: string): string {
  return texto.replace(/\*\*([^\s*](?:[^*\n]*[^\s*])?)\*\*/g, '$1');
}

/** El reloj de la grabación: «0:07», «1:05», «12:30». */
export function tiempoDeGrabacion(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

// ───────────────────────────── decisiones ─────────────────────────────

export interface ArchivoRechazado {
  nombre: string;
  motivo: string;
}

/**
 * De lo que se eligió o se soltó, qué se sube y qué no (con su porqué, para decírselo a la
 * persona). `cupo`: cuántos caben todavía en el mensaje.
 */
export function archivosAceptables<T extends { name: string; size: number }>(
  lista: readonly T[],
  tope: number = TOPE_DE_ADJUNTO,
  cupo: number = MAX_ADJUNTOS,
): { aceptados: T[]; rechazados: ArchivoRechazado[] } {
  const aceptados: T[] = [];
  const rechazados: ArchivoRechazado[] = [];
  for (const a of lista) {
    if (a.size <= 0) rechazados.push({ nombre: a.name, motivo: 'Está vacío.' });
    else if (a.size > tope) rechazados.push({ nombre: a.name, motivo: `Pesa ${tamanoLegible(a.size)}; el tope es ${tamanoLegible(tope)}.` });
    else if (aceptados.length >= cupo) rechazados.push({ nombre: a.name, motivo: `Ya no cabe: son hasta ${MAX_ADJUNTOS} archivos por mensaje.` });
    else aceptados.push(a);
  }
  return { aceptados, rechazados };
}

/** ¿Lo que viene arrastrado son archivos? (Un texto o una tarjeta arrastrada no enciende «Suelta aquí».) */
export function traeArchivos(tipos: Iterable<string> | null | undefined): boolean {
  return tipos ? [...tipos].includes('Files') : false;
}

/**
 * ¿Esta tecla manda el mensaje? Enter solo, en un equipo con teclado. Con Shift baja de línea;
 * en el teléfono Enter siempre baja de línea (se manda con el botón), y a media composición
 * (un acento, un teclado de otro idioma) Enter es de la composición.
 */
export function enterEnvia(tecla: { key: string; shiftKey: boolean; isComposing?: boolean }, tactil: boolean): boolean {
  return tecla.key === 'Enter' && !tecla.shiftKey && !tecla.isComposing && !tactil;
}

/** Por qué no se pudo grabar, dicho para la persona. `motivo`: 'sin-soporte' o el `name` del error del navegador. */
export function porQueNoGraba(motivo: string): string {
  if (motivo === 'sin-soporte') return 'Este navegador no deja grabar audio. Puedes adjuntar un audio con el clip.';
  if (motivo === 'NotAllowedError' || motivo === 'SecurityError') return 'No tengo permiso para usar el micrófono. Dámelo en los permisos del navegador y vuelve a tocar el micrófono.';
  if (motivo === 'NotFoundError' || motivo === 'OverconstrainedError') return 'No encuentro un micrófono en este equipo.';
  if (motivo === 'NotReadableError') return 'Otra aplicación está usando el micrófono. Ciérrala y vuelve a intentar.';
  return 'No pude empezar a grabar. Vuelve a intentar.';
}

/** El nombre del archivo de una grabación: la extensión sale de lo que grabó el navegador. */
export function nombreDeGrabacion(mime: string, ahora: Date): string {
  const ext = /mp4|m4a|aac/i.test(mime) ? 'm4a' : /ogg/i.test(mime) ? 'ogg' : /wav/i.test(mime) ? 'wav' : 'webm';
  const dos = (n: number) => String(n).padStart(2, '0');
  return `Nota de voz ${dos(ahora.getHours())}-${dos(ahora.getMinutes())}.${ext}`;
}
